import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { publishToMarketplace, getPublicationCenter, getRealityFilmStatus, retryRealityFilm } from "../api/doarPlatformApi";
import { workspaceAccountScope } from '../core/workspace/workspaceAccountScope';
import { SPACE_ORIGIN } from '../utils/productOrigins';
import "./Marketplace.css";
import "./MarketplacePublishMobile.css";

const PUBLISH_PENDING_PREFIX = "doar_publish_pending_v3:";
export const MAX_FILM_STATUS_POLLS = 60;
const validSlug = slug => typeof slug === 'string' && /^[a-z0-9][a-z0-9_-]{0,179}$/.test(slug);

function pendingKey(objectId) {
  return `${PUBLISH_PENDING_PREFIX}${String(objectId || "unknown")}`;
}

export default function PublishMarketplacePage() {
  const auth = useContext(AuthContext);
  const { token, loading: authLoading } = auth;
  const scope = workspaceAccountScope(auth);
  const live = useRef(scope), mounted = useRef(true), operation = useRef(null), pendingContent = useRef(null), retryRevision = useRef(null);
  live.current = scope;
  const isCurrent = useCallback(() => mounted.current && live.current === scope, [scope]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const objectId = params.get("object") || params.get("doar");
  const initialClaim = params.get("claim");
  const expectedRevisionId = params.get("revision");
  const [saving, setSaving] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [filmStage, setFilmStage] = useState("");
  const [canRetryFilm, setCanRetryFilm] = useState(false);
  const [error, setError] = useState("");
  const [claimPending, setClaimPending] = useState(Boolean(token && initialClaim));
  const [claimFailed, setClaimFailed] = useState(false);
  const key = pendingKey(objectId);
  useEffect(() => { operation.current = null; pendingContent.current = null; retryRevision.current = null; setPreparing(false); setSaving(false); setCanRetryFilm(false); setFilmStage(''); setError(''); }, [scope]);

  useEffect(() => {
    if (!token || !initialClaim) {
      setClaimPending(false);
      return undefined;
    }

    setClaimPending(true);
    const onClaimed = () => {
      setClaimPending(false);
      setClaimFailed(false);
      setError("");
    };
    const onClaimFailed = () => {
      setClaimPending(false);
      setClaimFailed(true);
      setError("This DoAR could not be saved to your account. Return to ToDoAR and try again.");
    };

    window.addEventListener("doar:guest-claimed", onClaimed);
    window.addEventListener("doar:guest-claim-failed", onClaimFailed);

    const settleTimer = window.setTimeout(() => {
      if (!new URLSearchParams(window.location.search).get("claim")) {
        setClaimPending(false);
        setClaimFailed(false);
      }
    }, 1200);
    const failClosedTimer = window.setTimeout(() => {
      if (!new URLSearchParams(window.location.search).get("claim")) return;
      setClaimPending(false);
      setClaimFailed(true);
      setError("This DoAR handoff did not finish. Return to ToDoAR and try Publish again.");
    }, 8000);

    return () => {
      window.clearTimeout(settleTimer);
      window.clearTimeout(failClosedTimer);
      window.removeEventListener("doar:guest-claimed", onClaimed);
      window.removeEventListener("doar:guest-claim-failed", onClaimFailed);
    };
  }, [token, initialClaim]);

  const publishNow = useCallback(async () => {
    if (!token || !objectId || operation.current || claimPending || claimFailed) return;
    const request = {}; operation.current = request;

    setSaving(true);
    setError("");
    try {
      const result = await publishToMarketplace(
        objectId,
        { rightsAttested: true, ...(expectedRevisionId ? { expectedRevisionId } : {}) },
        { timeout: 20000 }
      );
      if (!isCurrent()) return;
      try { sessionStorage.removeItem(key); } catch {}
      if (result?.publicationPending === true) {
        pendingContent.current = result.film?.contentId || null;
        retryRevision.current = result.revisionId || expectedRevisionId;
        setCanRetryFilm(false);
        setPreparing(true);
        setFilmStage("Preparing Image, 3D and five-second Reality Film…");
        return;
      }
      const slug = result?.listing?.slug;
      if (!validSlug(slug) || result?.listing?.status !== 'published' || !result?.listing?.revisionId || (expectedRevisionId && result.listing.revisionId !== expectedRevisionId)) throw new Error("Publication was not confirmed for the reviewed revision.");
      window.location.assign(`/r/${encodeURIComponent(slug)}`);
    } catch (err) {
      if (!isCurrent()) return;
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message || err?.message || "Could not publish this Reality.";
      setError(code ? `${message} (${code})` : message);
    } finally {
      if (operation.current === request) operation.current = null;
      if (isCurrent()) setSaving(false);
    }
  }, [token, objectId, claimPending, claimFailed, key, expectedRevisionId, isCurrent]);

  useEffect(() => {
    if (!preparing || !token || !objectId) return undefined;
    let live = true;
    let busy = false;
    let attempts = 0;
    const cancellation = new AbortController();
    const current = () => live && isCurrent();
    const stop = (message, retryable = false) => { if (!current()) return; live = false; cancellation.abort(); setPreparing(false); setCanRetryFilm(retryable); setError(message); };
    const poll = async () => {
      if (!current() || busy) return;
      if (attempts++ >= MAX_FILM_STATUS_POLLS) { stop('Film preparation is still pending. Check its status again when ready.'); return; }
      busy = true;
      try {
        const film = await getRealityFilmStatus(objectId, {signal:cancellation.signal});
        if (!current()) return;
        retryRevision.current = film.revisionId || retryRevision.current;
        if (pendingContent.current && film.contentId && pendingContent.current !== film.contentId) { stop('Another revision is being prepared. Review the current saved Reality before continuing.'); return; }
        if (film.status === "ready") {
          const published = await getPublicationCenter(objectId, {signal:cancellation.signal});
          if (!current()) return;
          const listing = published?.listing;
          if (published?.object?.id === objectId && published.revisionId === film.revisionId && listing?.revisionId === film.revisionId && listing.status === 'published' && validSlug(listing.slug)) {
            live = false;
            window.location.assign(`/r/${encodeURIComponent(listing.slug)}`);
            return;
          }
          stop('Film is ready but publication of that revision is not confirmed. Review publication before continuing.'); return;
        }
        if (["failed", "superseded", "disabled", "unavailable", "not_started", "not_found"].includes(film.status)) {
          setFilmStage("Reality Film status needs attention.");
          stop(({superseded:'This Film belongs to a superseded revision. Review the current Reality.',disabled:'The Film worker is disabled or unavailable.',unavailable:'The Film source or delivery is unavailable.',not_started:'No Film has been requested for this revision.',not_found:'The owned Reality is unavailable.'})[film.status] || film.errorCode || 'The Film failed. Retry only its authorized preparation.', film.status === 'failed' && film.retryable === true);
          return;
        }
        setFilmStage({
          preparing_model: "Preparing your 3D Reality model…",
          queued: "Reality Film queued…",
          rendering: "Rendering your five-second Reality Film…",
          attaching: "Finalizing Image, Film and publication…",
          preparing: "Preparing your Reality Film…",
        }[film.status] || "Finishing your Reality…");
      } catch (error) {
        if (current()) setFilmStage("Reconnecting to Reality Film preparation…");
      } finally { busy = false; }
    };
    poll();
    const timer = window.setInterval(poll, 5000);
    const deadline = window.setTimeout(() => stop('Film preparation is still pending. Check its status again when ready.'), MAX_FILM_STATUS_POLLS * 5000);
    return () => { live = false; cancellation.abort(); window.clearInterval(timer); window.clearTimeout(deadline); };
  }, [preparing, token, objectId, isCurrent]);

  const continueToAccount = useCallback(() => {
    if (!objectId) return;
    setError("");
    try { sessionStorage.setItem(key, JSON.stringify({revision:expectedRevisionId,scope:token ? scope : null})); } catch {}
    const next = `${window.location.pathname}${window.location.search}${window.location.hash || ""}`;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }, [objectId, key, expectedRevisionId, token, scope]);

  useEffect(() => {
    if (!token || authLoading || claimPending || claimFailed || saving || !objectId) return;
    let pending = false;
    try { const saved = JSON.parse(sessionStorage.getItem(key)); pending = saved && saved.revision === expectedRevisionId && (saved.scope === null || saved.scope === scope); } catch {}
    if (!pending) return;
    if (new URLSearchParams(window.location.search).get("claim")) return;
    try { sessionStorage.removeItem(key); } catch {}
    publishNow();
  }, [token, authLoading, claimPending, claimFailed, saving, objectId, key, publishNow, scope, expectedRevisionId]);

  if (!objectId) {
    return (
      <main className="market-state">
        <div>
          <a className="market-logo" href="/">DoAR</a>
          <h1>Nothing to publish yet.</h1>
          <p>Open a DoAR, then tap Publish.</p>
          <a href="https://todoar.co">Create a DoAR →</a>
        </div>
      </main>
    );
  }

  const onPublish = async () => {
    if (operation.current || saving || preparing || claimPending || claimFailed) return;
    if (!token) {
      continueToAccount();
      return;
    }
    if (error && filmStage) {
      if (!canRetryFilm) { setError(''); setPreparing(true); return; }
      const request = {}; operation.current = request; setSaving(true);
      setError("");
      try {
        const result = await retryRealityFilm(objectId, retryRevision.current || expectedRevisionId);
        if (!isCurrent()) return;
        if (!['queued','preparing','preparing_model','rendering','attaching','published','already_published','syncing','already_claimed'].includes(result?.state)) throw new Error('This Film retry was not admitted. Review the current publication.');
        setCanRetryFilm(false); setPreparing(true);
      } catch (err) { if (isCurrent()) setError(err?.response?.data?.message || err.message || 'Could not retry this Film.'); }
      finally { if (operation.current === request) operation.current = null; if (isCurrent()) setSaving(false); }
      return;
    }
    publishNow();
  };

  const busy = saving || preparing || claimPending || authLoading;
  const label = preparing
    ? "Preparing Reality…"
    : claimPending
    ? "Saving your DoAR…"
    : saving
      ? "Publishing…"
      : token
        ? error && filmStage ? canRetryFilm ? 'Retry Film preparation' : 'Check Film status' : "Publish"
        : "Sign in & publish";

  return (
    <main className="market-publish-page publication-one-click">
      <header className="publication-header publication-one-click__header">
        <a className="market-logo" href="/">DoAR</a>
        <a href="/">Cancel</a>
      </header>

      <section className="publication-one-click__body">
        <p className="market-eyebrow">Publish Reality</p>
        <h1>Ready to publish?</h1>
        {expectedRevisionId && <p>Reviewed saved revision: <code>{expectedRevisionId}</code></p>}
        <p className="publication-one-click__lead">
          Publish this saved revision on TheDoAR. Only verified, revision-pinned outputs are made public. Original files and later edits stay private until you choose to publish a new revision.
        </p>

        <button
          type="button"
          className="publication-one-click__button"
          onClick={onPublish}
          disabled={busy || claimFailed}
        >
          {label}
        </button>

        <p className="publication-one-click__rights">
          By publishing, you confirm you have the right to share this material.
        </p>

        {preparing ? <p role="status" aria-live="polite">{filmStage}</p> : null}
        {claimFailed ? (
          <div className="publication-one-click__recovery">
            <p className="market-form-error">{error}</p>
            <button type="button" onClick={() => window.history.back()}>Back to ToDoAR</button>
          </div>
        ) : null}
        {!claimFailed && error ? <><p className="market-form-error" role="alert">{error}</p><a href={`${SPACE_ORIGIN}/d/${encodeURIComponent(objectId)}`}>Review saved Reality in DoARspace</a></> : null}
      </section>
    </main>
  );
}
